import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';

@Component({
  selector: 'app-admin-tier-form',
  standalone: false,
  templateUrl: './admin-tier-form.html',
  styleUrl: './admin-tier-form.css',
})
export class AdminTierForm implements OnInit {
  form!: FormGroup;
  loading = false;
  saving = false;
  lookupsLoading = false;
  error: string | null = null;

  id = '';
  copyFromId = '';
  isEditMode = false;
  isCopyMode = false;

  jurisdictions: any[] = [];
  existingTiers: any[] = [];

  customerTypes = [
    { label: 'Individual', value: 'Individual' },
    { label: 'Business', value: 'Business' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private notifyService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    this.copyFromId = this.route.snapshot.queryParamMap.get('copyFrom') || '';
    this.isEditMode = !!this.id;
    this.isCopyMode = !this.isEditMode && !!this.copyFromId;

    this.form = this.fb.group({
      jurisdictionId: ['', [Validators.required]],
      customerType: ['', [Validators.required]],
      tierCode: ['', [Validators.required, Validators.maxLength(30)]],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      rank: [1, [Validators.required, Validators.min(1)]],
      isDefault: [false],
      isActive: [true]
    });

    this.loadLookups();
  }

  loadLookups(): void {
    this.lookupsLoading = true;
    this.error = null;

    forkJoin({
      jurisdictions: this.adminService.getJurisdictions({ page: 1, pageSize: 500 }),
      tiers: this.adminService.getTiers({ page: 1, pageSize: 1000 })
    })
      .pipe(finalize(() => (this.lookupsLoading = false)))
      .subscribe({
        next: (res: any) => {
          this.jurisdictions = res?.jurisdictions?.data || res?.jurisdictions || [];
          this.existingTiers = res?.tiers?.data || res?.tiers || [];

          if (this.isEditMode) {
            this.load(this.id);
          } else if (this.isCopyMode) {
            this.load(this.copyFromId, true);
          }
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load lookup data.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  load(targetId: string, isCopy = false): void {
    if (!targetId) return;

    this.loading = true;
    this.error = null;

    this.adminService.getTierById(targetId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          const item = res?.data || res;

          if (!item) {
            this.error = 'Tier not found.';
            return;
          }

          this.form.patchValue({
            jurisdictionId: item.jurisdictionId || '',
            customerType: item.customerType || '',
            tierCode: item.tierCode || '',
            name: item.name || '',
            rank: item.rank ?? 1,
            isDefault: isCopy ? false : (item.isDefault ?? false),
            isActive: item.isActive ?? true
          });

          if (isCopy) {
            this.notifyService.showInfo(
              'Tier copied. Change jurisdiction, customer type, or tier code before saving.',
              'Copy Mode'
            );
          }
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load tier.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  hasDuplicateCombination(): boolean {
    const jurisdictionId = String(this.form.value.jurisdictionId || '').trim();
    const customerType = String(this.form.value.customerType || '').trim();
    const tierCode = String(this.form.value.tierCode || '').trim().toUpperCase();

    return this.existingTiers.some((x: any) => {
      if (!x) return false;

      const same =
        String(x.jurisdictionId || '').trim() === jurisdictionId &&
        String(x.customerType || '').trim() === customerType &&
        String(x.tierCode || '').trim().toUpperCase() === tierCode;

      if (!same) return false;

      if (this.isEditMode) {
        return String(x.id) !== String(this.id);
      }

      return true;
    });
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    if (this.hasDuplicateCombination()) {
      this.notifyService.showError(
        'A tier with this jurisdiction, tier code, and customer type already exists.',
        'Duplicate Tier'
      );
      return;
    }

    this.saving = true;
    this.error = null;

    const payload = {
      jurisdictionId: String(this.form.value.jurisdictionId || '').trim(),
      customerType: String(this.form.value.customerType || '').trim(),
      tierCode: String(this.form.value.tierCode || '').trim().toUpperCase(),
      name: String(this.form.value.name || '').trim(),
      rank: Number(this.form.value.rank || 1),
      isDefault: !!this.form.value.isDefault,
      isActive: !!this.form.value.isActive
    };

    const request$ = this.isEditMode
      ? this.adminService.updateTier(this.id, payload)
      : this.adminService.createTier(payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess(
            this.isCopyMode ? 'Tier copied successfully.' : 'Tier saved successfully.',
            'Success'
          );
          this.router.navigate(['/admin/compliance/tiers']);
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save tier.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/admin/compliance/tiers']);
  }

  get jurisdictionId() { return this.form.get('jurisdictionId'); }
  get customerType() { return this.form.get('customerType'); }
  get tierCode() { return this.form.get('tierCode'); }
  get name() { return this.form.get('name'); }
  get rank() { return this.form.get('rank'); }
}
